// Upload: Drag & Drop (auch Ordner), Dateiauswahl, Ordnerauswahl, Kamera (Handy). ZIP-Dateien werden
// auf dem Server entpackt. Ohne festen Standort ordnet der Server über Ordner-/Dateinamen zu.
import { useRef, useState, type DragEvent } from 'react';
import type { DbPortalUploadErgebnis } from '@gql/shared-types';
import { ART_TEXT, ausAblage, ausAuswahl, inPakete, MAX_DATEI_MB, sendePaket, type Auswahl } from '../daten/lieferungen.js';
import { KERN_BIS, KERN_VON } from '../daten/zeitreihe.js';

interface Props {
  /** null = mehrere Standorte (Zuordnung über Ordner/Dateinamen) */
  standortId: string | null;
  onHochgeladen: (e: DbPortalUploadErgebnis) => void;
}

const JAHRE = Array.from({ length: KERN_BIS - KERN_VON + 1 }, (_, i) => KERN_VON + i);

export function UploadBereich({ standortId, onHochgeladen }: Props) {
  const [ziehen, setZiehen] = useState(false);
  const [auswahl, setAuswahl] = useState<Auswahl[]>([]);
  const [art, setArt] = useState('');
  const [jahr, setJahr] = useState('');
  const [kommentar, setKommentar] = useState('');
  const [laeuft, setLaeuft] = useState<{ anteil: number; paket: number; pakete: number } | null>(null);
  const [ergebnis, setErgebnis] = useState<DbPortalUploadErgebnis | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const kamera = useRef(false);

  const hinzufuegen = (neu: Auswahl[], vonKamera = false) => {
    kamera.current = vonKamera;
    setErgebnis(null);
    setFehler(null);
    const zuGross = neu.filter((a) => a.datei.size > MAX_DATEI_MB * 1024 * 1024);
    if (zuGross.length) setFehler(`${zuGross.map((a) => a.datei.name).join(', ')}: größer als ${MAX_DATEI_MB} MB – bitte aufteilen.`);
    setAuswahl((alt) => [...alt, ...neu.filter((a) => !zuGross.includes(a))]);
  };

  async function ablegen(e: DragEvent) {
    e.preventDefault();
    setZiehen(false);
    hinzufuegen(await ausAblage(e.dataTransfer));
  }

  async function senden() {
    const pfad = standortId ? `/standorte/${standortId}/dokumente` : '/dokumente';
    const pakete = inPakete(auswahl);
    const gesamt: DbPortalUploadErgebnis = { lieferungen: [], angenommen: [], abgewiesen: [] };
    setFehler(null);
    try {
      for (const [i, paket] of pakete.entries()) {
        setLaeuft({ anteil: 0, paket: i + 1, pakete: pakete.length });
        const r = await sendePaket(pfad, paket, { art, jahr, kommentar, herkunft: kamera.current ? 'kamera' : 'datei' }, (anteil) =>
          setLaeuft({ anteil, paket: i + 1, pakete: pakete.length }),
        );
        gesamt.lieferungen.push(...r.lieferungen);
        gesamt.angenommen.push(...r.angenommen);
        gesamt.abgewiesen.push(...r.abgewiesen);
      }
      setAuswahl([]);
      setKommentar('');
      setErgebnis(gesamt);
      onHochgeladen(gesamt);
    } catch (f) {
      setFehler((f as Error).message);
      if (gesamt.angenommen.length) {
        setErgebnis(gesamt);
        onHochgeladen(gesamt);
      }
    } finally {
      setLaeuft(null);
    }
  }

  return (
    <div className="upload">
      <p className="hinweis-klein">
        Bitte laden Sie <strong>alles</strong> hoch, was zum Standort vorliegt – das Portal sortiert selbst: Verträge und
        Vertragsformulare, AGB bzw. Vertragsbedingungen, Leistungsbeschreibungen, Auftragsbestätigungen, Rechnungen und
        Gutschriften, Mitteilungen über geänderte Entgelte sowie Aufstellungen mit Einzelpreisen (z. B. „Vertragsunterlage“).
      </p>
      <div
        className={`ablage ablage-gross${ziehen ? ' ist-aktiv' : ''}`}
        onDragOver={(e) => {
          e.preventDefault();
          setZiehen(true);
        }}
        onDragLeave={() => setZiehen(false)}
        onDrop={(e) => void ablegen(e)}
      >
        <input
          type="file"
          multiple
          accept=".pdf,.jpg,.jpeg,.png,.tif,.tiff,.heic,.zip,application/pdf,image/*,application/zip"
          aria-label="Dateien auswählen"
          onChange={(e) => {
            hinzufuegen(ausAuswahl(e.target.files));
            e.target.value = '';
          }}
        />
        <span className="ablage-text">
          <strong>Dateien hierher ziehen</strong> oder tippen zum Auswählen
          <br />
          PDF, Fotos (JPG, PNG, HEIC), Scans (TIFF) oder ZIP · bis {MAX_DATEI_MB} MB je Datei
          {!standortId && (
            <>
              <br />
              Ganze Ordner: je Standort ein Ordner, benannt mit der Externen ID oder dem Standortnamen
            </>
          )}
        </span>
      </div>

      <div className="knopfreihe upload-knoepfe">
        <label className="knopf-sekundaer knopf-datei">
          <input
            type="file"
            accept="image/*"
            capture="environment"
            onChange={(e) => {
              hinzufuegen(ausAuswahl(e.target.files), true);
              e.target.value = '';
            }}
          />
          Foto aufnehmen
        </label>
        <label className="knopf-sekundaer knopf-datei nur-zeiger">
          <input
            type="file"
            multiple
            {...({ webkitdirectory: '' } as Record<string, string>)}
            onChange={(e) => {
              hinzufuegen(ausAuswahl(e.target.files));
              e.target.value = '';
            }}
          />
          Ordner auswählen
        </label>
      </div>

      {auswahl.length > 0 && (
        <div className="kasten upload-auswahl">
          <h3>
            {auswahl.length} {auswahl.length === 1 ? 'Datei' : 'Dateien'} bereit
          </h3>
          <ul className="upload-liste">
            {auswahl.slice(0, 12).map((a, i) => (
              <li key={`${a.pfad}-${i}`}>
                <span className="upload-name">{a.pfad}</span>
                <button type="button" className="knopf-klein-text" onClick={() => setAuswahl(auswahl.filter((_, j) => j !== i))} aria-label={`${a.pfad} entfernen`}>
                  Entfernen
                </button>
              </li>
            ))}
            {auswahl.length > 12 && <li className="zelle-hinweis">… und {auswahl.length - 12} weitere</li>}
          </ul>
          {standortId && (
            <div className="filterzeile">
              <label className="feld">
                <span className="feld-label">Art (optional)</span>
                <select value={art} onChange={(e) => setArt(e.target.value)}>
                  <option value="">automatisch erkennen</option>
                  {Object.entries(ART_TEXT).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                </select>
              </label>
              <label className="feld">
                <span className="feld-label">Jahr (optional)</span>
                <select value={jahr} onChange={(e) => setJahr(e.target.value)}>
                  <option value="">automatisch erkennen</option>
                  {JAHRE.map((j) => (
                    <option key={j} value={j}>
                      {j}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}
          <label className="feld feld-breit">
            <span className="feld-label">Anmerkung für GQL (optional)</span>
            <input type="text" maxLength={1000} value={kommentar} onChange={(e) => setKommentar(e.target.value)} />
          </label>
          <div className="knopfreihe">
            <button type="button" className="knopf" disabled={!!laeuft} onClick={() => void senden()}>
              {laeuft ? 'Wird hochgeladen …' : 'Hochladen'}
            </button>
            <button type="button" className="knopf-sekundaer" disabled={!!laeuft} onClick={() => setAuswahl([])}>
              Auswahl leeren
            </button>
          </div>
          {laeuft && (
            <div className="upload-fortschritt" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(laeuft.anteil * 100)} aria-label="Upload-Fortschritt">
              <span style={{ width: `${Math.round(laeuft.anteil * 100)}%` }} />
              <em>
                {laeuft.pakete > 1 ? `Teil ${laeuft.paket} von ${laeuft.pakete}: ` : ''}
                {Math.round(laeuft.anteil * 100)} %
              </em>
            </div>
          )}
        </div>
      )}

      {fehler && (
        <p className="fehlertext" role="alert">
          {fehler}
        </p>
      )}
      {ergebnis && (
        <div className="hinweis-kasten" role="status">
          <p>
            <strong>
              {ergebnis.angenommen.length} {ergebnis.angenommen.length === 1 ? 'Datei' : 'Dateien'} übernommen
            </strong>
            {ergebnis.angenommen.length > 0 && ' – die Auswertung läuft, das Ergebnis erscheint unten.'}
          </p>
          {ergebnis.abgewiesen.length > 0 && (
            <>
              <p>Nicht übernommen:</p>
              <ul className="upload-abgewiesen">
                {ergebnis.abgewiesen.map((a, i) => (
                  <li key={i}>
                    <span className="upload-name">{a.zipPfad ?? a.dateiname}</span>: {a.grund}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}
